import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api.service';
import { useAuth } from '../contexts/AuthContext';
import { PageTitle } from '@trinetra/shared';

interface AlertConfig {
  enabled: boolean;
  smtp_host: string;
  smtp_port: number;
  smtp_secure: boolean;
  smtp_user: string;
  smtp_pass: string;
  from_email: string;
  from_name: string;
  recipients: string[];
  cooldown_minutes: number;
  offline_threshold_seconds: number;
  resend_api_key: string;
}

interface AlertLog {
  id: number;
  node_name: string;
  alert_type: string;
  recipients: string[];
  success: boolean;
  error_message: string | null;
  sent_at: string;
}

const defaultConfig: AlertConfig = {
  enabled: false,
  smtp_host: '',
  smtp_port: 587,
  smtp_secure: false,
  smtp_user: '',
  smtp_pass: '',
  from_email: '',
  from_name: 'TRINETRA Alert System',
  recipients: [],
  cooldown_minutes: 15,
  offline_threshold_seconds: 120,
  resend_api_key: '',
};

export default function AlertConfigPage() {
  const { hasPermission } = useAuth();
  const canConfig = hasPermission('alerts.config'); // backend also enforces
  const [config, setConfig] = useState<AlertConfig>(defaultConfig);
  const [logs, setLogs] = useState<AlertLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [recipientInput, setRecipientInput] = useState('');

  const fetchConfig = useCallback(async () => {
    try {
      const { data } = await (api as any).get('/alerts/config');
      if (data.success && data.data) {
        setConfig({
          ...defaultConfig,
          ...data.data,
          recipients: Array.isArray(data.data.recipients) ? data.data.recipients : [],
        });
      }
    } catch (err) {
      console.error('Failed to fetch alert config', err);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      const { data } = await (api as any).get('/alerts/log?limit=30');
      if (data.success) setLogs(data.data || []);
    } catch (err) {
      console.error('Failed to fetch alert logs', err);
    }
  }, []);

  useEffect(() => {
    Promise.all([fetchConfig(), fetchLogs()]).finally(() => setLoading(false));
  }, [fetchConfig, fetchLogs]);

  const showMsg = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await (api as any).put('/alerts/config', config);
      if (data.success) {
        setConfig({ ...defaultConfig, ...data.data, recipients: data.data.recipients || [] });
        showMsg('success', 'Alert configuration saved successfully');
      } else {
        showMsg('error', data.error || 'Save failed');
      }
    } catch (err: any) {
      showMsg('error', err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) { showMsg('error', 'Enter a test email address'); return; }
    setTesting(true);
    try {
      const { data } = await (api as any).post('/alerts/test', { email: testEmail });
      if (data.success) {
        showMsg('success', data.message);
        fetchLogs();
      } else {
        showMsg('error', data.error || 'Test failed');
      }
    } catch (err: any) {
      showMsg('error', err.message || 'Failed to send test email');
    } finally {
      setTesting(false);
    }
  };

  const addRecipient = () => {
    const email = recipientInput.trim();
    if (email && !config.recipients.includes(email)) {
      setConfig(c => ({ ...c, recipients: [...c.recipients, email] }));
      setRecipientInput('');
    }
  };

  const removeRecipient = (email: string) => {
    setConfig(c => ({ ...c, recipients: c.recipients.filter(r => r !== email) }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-brand" />
      </div>
    );
  }

  const inputCls = 'w-full px-3 py-2 rounded-lg border bg-[var(--card-bg)] border-[var(--card-border)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-brand/50';
  const labelCls = 'block text-sm font-medium text-[var(--text-secondary)] mb-1';
  const cardCls = 'rounded-xl border p-4 bg-[var(--card-bg)] border-[var(--card-border)]';

  return (
    <div className="space-y-3 max-w-4xl mx-auto">
      <PageTitle title="Alert Configuration" subtitle="Configure email alerts for node-down notifications">
        <label className="flex items-center gap-3 cursor-pointer">
          <span className="text-sm font-medium text-[var(--text-secondary)]">
            {config.enabled ? 'Enabled' : 'Disabled'}
          </span>
          <div
            className={`relative w-12 h-6 rounded-full transition-colors ${config.enabled ? 'bg-green-500' : 'bg-gray-400'}`}
            onClick={() => setConfig(c => ({ ...c, enabled: !c.enabled }))}
          >
            <div
              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${config.enabled ? 'translate-x-6' : ''}`}
            />
          </div>
        </label>
      </PageTitle>

      {message && (
        <div className={`px-4 py-3 rounded-lg text-sm font-medium ${message.type === 'success' ? 'bg-green-500/15 text-green-400 border border-green-500/30' : 'bg-red-500/15 text-red-400 border border-red-500/30'}`}>
          {message.text}
        </div>
      )}

      {/* Email Provider */}
      <div className={cardCls}>
        <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-4">Email Provider</h2>
        <p className="text-sm text-[var(--text-muted)] mb-4">
          Use <strong>Resend</strong> (recommended — works on all servers) or SMTP. If Resend API key is set, SMTP is ignored.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Resend API Key</label>
            <input className={inputCls} type="password" placeholder="re_xxxxxxxxx" value={config.resend_api_key}
              onChange={e => setConfig(c => ({ ...c, resend_api_key: e.target.value }))} />
            <p className="text-xs text-[var(--text-muted)] mt-1">
              Get one free at <a href="https://resend.com" target="_blank" rel="noreferrer" className="text-brand hover:underline">resend.com</a> (100 emails/day free)
            </p>
          </div>
          <div>
            <label className={labelCls}>From Email</label>
            <input className={inputCls} placeholder="alerts@yourdomain.com" value={config.from_email}
              onChange={e => setConfig(c => ({ ...c, from_email: e.target.value }))} />
            <p className="text-xs text-[var(--text-muted)] mt-1">
              For Resend: use <code className="text-xs">onboarding@resend.dev</code> to test, or verify your domain
            </p>
          </div>
          <div>
            <label className={labelCls}>From Name</label>
            <input className={inputCls} placeholder="TRINETRA Alert System" value={config.from_name}
              onChange={e => setConfig(c => ({ ...c, from_name: e.target.value }))} />
          </div>
        </div>
      </div>

      {/* SMTP Settings (alternative) */}
      <div className={cardCls}>
        <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-4">SMTP Settings <span className="text-sm font-normal text-[var(--text-muted)]">(alternative — used only if Resend key is empty)</span></h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>SMTP Host</label>
            <input className={inputCls} placeholder="smtp.gmail.com" value={config.smtp_host}
              onChange={e => setConfig(c => ({ ...c, smtp_host: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Port</label>
              <input className={inputCls} type="number" value={config.smtp_port}
                onChange={e => setConfig(c => ({ ...c, smtp_port: parseInt(e.target.value) || 587 }))} />
            </div>
            <div>
              <label className={labelCls}>Secure (TLS)</label>
              <div className="flex items-center h-[42px]">
                <input type="checkbox" className="w-5 h-5 rounded accent-[var(--brand)]" checked={config.smtp_secure}
                  onChange={e => setConfig(c => ({ ...c, smtp_secure: e.target.checked }))} />
                <span className="ml-2 text-sm text-[var(--text-muted)]">SSL/TLS</span>
              </div>
            </div>
          </div>
          <div>
            <label className={labelCls}>SMTP Username</label>
            <input className={inputCls} placeholder="user@gmail.com" value={config.smtp_user}
              onChange={e => setConfig(c => ({ ...c, smtp_user: e.target.value }))} />
          </div>
          <div>
            <label className={labelCls}>SMTP Password</label>
            <input className={inputCls} type="password" placeholder="App password" value={config.smtp_pass}
              onChange={e => setConfig(c => ({ ...c, smtp_pass: e.target.value }))} />
          </div>
        </div>
      </div>

      {/* Recipients */}
      <div className={cardCls}>
        <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-4">Alert Recipients</h2>
        <div className="flex gap-2 mb-3">
          <input className={inputCls} placeholder="email@example.com" value={recipientInput}
            onChange={e => setRecipientInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addRecipient())} />
          <button onClick={addRecipient}
            className="px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand/80 transition whitespace-nowrap text-sm font-medium">
            Add
          </button>
        </div>
        {config.recipients.length === 0 ? (
          <p className="text-sm text-[var(--text-muted)]">No recipients configured</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {config.recipients.map(email => (
              <span key={email} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm bg-brand/15 text-brand border border-brand/30">
                {email}
                <button onClick={() => removeRecipient(email)}
                  className="hover:text-red-400 transition text-lg leading-none">&times;</button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Thresholds */}
      <div className={cardCls}>
        <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-4">Alert Thresholds</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Offline Threshold (seconds)</label>
            <input className={inputCls} type="number" min={30} value={config.offline_threshold_seconds}
              onChange={e => setConfig(c => ({ ...c, offline_threshold_seconds: parseInt(e.target.value) || 120 }))} />
            <p className="text-xs text-[var(--text-muted)] mt-1">Time before a site is considered offline for alerting</p>
          </div>
          <div>
            <label className={labelCls}>Cooldown (minutes)</label>
            <input className={inputCls} type="number" min={1} value={config.cooldown_minutes}
              onChange={e => setConfig(c => ({ ...c, cooldown_minutes: parseInt(e.target.value) || 15 }))} />
            <p className="text-xs text-[var(--text-muted)] mt-1">Minimum time between repeated alerts for the same site</p>
          </div>
        </div>
      </div>

      {/* Save + Test */}
      {canConfig && (
      <div className={cardCls}>
        <div className="flex flex-col md:flex-row items-start md:items-end gap-4">
          <div className="flex-1">
            <label className={labelCls}>Test Email Address</label>
            <input className={inputCls} placeholder="your@email.com" value={testEmail}
              onChange={e => setTestEmail(e.target.value)} />
          </div>
          <button onClick={handleTestEmail} disabled={testing}
            className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-500 transition text-sm font-medium disabled:opacity-50 whitespace-nowrap">
            {testing ? 'Sending…' : 'Send Test Email'}
          </button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand/80 transition text-sm font-medium disabled:opacity-50 whitespace-nowrap">
            {saving ? 'Saving…' : 'Save Configuration'}
          </button>
        </div>
      </div>
      )}

      {/* Alert Log */}
      <div className={cardCls}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">Alert Log</h2>
          <button onClick={fetchLogs} className="text-sm text-brand hover:underline">Refresh</button>
        </div>
        {logs.length === 0 ? (
          <p className="text-sm text-[var(--text-muted)]">No alerts sent yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--card-border)]">
                  <th className="text-left py-2 px-2 text-[var(--text-muted)] font-medium">Time</th>
                  <th className="text-left py-2 px-2 text-[var(--text-muted)] font-medium">Site</th>
                  <th className="text-left py-2 px-2 text-[var(--text-muted)] font-medium">Type</th>
                  <th className="text-left py-2 px-2 text-[var(--text-muted)] font-medium">Recipients</th>
                  <th className="text-left py-2 px-2 text-[var(--text-muted)] font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log.id} className="border-b border-[var(--card-border)] last:border-0">
                    <td className="py-2 px-2 text-[var(--text-secondary)] whitespace-nowrap">
                      {new Date(log.sent_at).toLocaleString()}
                    </td>
                    <td className="py-2 px-2 text-[var(--text-primary)]">{log.node_name || '—'}</td>
                    <td className="py-2 px-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        log.alert_type === 'test' ? 'bg-blue-500/15 text-blue-400' : 'bg-red-500/15 text-red-400'
                      }`}>
                        {log.alert_type}
                      </span>
                    </td>
                    <td className="py-2 px-2 text-[var(--text-muted)] text-xs">
                      {Array.isArray(log.recipients) ? log.recipients.join(', ') : String(log.recipients)}
                    </td>
                    <td className="py-2 px-2">
                      {log.success ? (
                        <span className="text-green-400 text-xs font-medium">✓ Sent</span>
                      ) : (
                        <span className="text-red-400 text-xs font-medium" title={log.error_message || ''}>✗ Failed</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
